import * as THREE from 'three';
import { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import Crosshair3D from './Crosshair3D';
import { Miura } from '../models/miura5';

// Shared fail endpoint logic for both RocketFailCurve and RocketShip
function getFailEndPoint(start, end, seed) {
  function seededRandom(seed) {
    let x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  }
  // Pick a random side: 0=left, 1=right, 2=bottom, 3=top
  const combinedSeed = seed + start[0] * 10007 + start[1] * 10009 + start[2] * 10037;
  const side = Math.floor(seededRandom(combinedSeed) * 4);
  let x, y;
  if (side === 0) { // left
    x = -10 - seededRandom(combinedSeed + 1) * 2; // -10 to -12
    y = -10 + seededRandom(combinedSeed + 2) * 20;
  } else if (side === 1) { // right
    x = 10 + seededRandom(combinedSeed + 1) * 2; // 10 to 12
    y = -10 + seededRandom(combinedSeed + 2) * 20;
  } else if (side === 2) { // bottom
    x = -10 + seededRandom(combinedSeed + 1) * 20;
    y = -10 - seededRandom(combinedSeed + 2) * 2; // -10 to -12
  } else { // top
    x = -10 + seededRandom(combinedSeed + 1) * 20;
    y = 10 + seededRandom(combinedSeed + 2) * 2; // 10 to 12
  }
  // Clamp x and y to [-12, 12]
  x = Math.max(-5, Math.min(5, x));
  y = Math.max(-5, Math.min(5, y));
  // Always set z to 40
  return [x, y, -20];
}

const RocketShip = forwardRef(function RocketShip({ 
  position = [0, -2, -5], 
  rotation = [0, 0, 0],
  firing = false,
  targetPosition = [0, 0, -20],
  cakeRef,
  floatRotationIntensity = 2,
  resetGame,
  resetCounter = 0,
  onMiss,
  paused = false,
  gameComplete = false,
  onHitAnimationComplete,
  curveSeed = 0,
  fail = false
}, ref) {
  // Track spin angle in state so it persists across Float and manual animation
  const [spin, setSpin] = useState(0);
  const rocketRef = useRef();
  const [startPosition, setStartPosition] = useState(position);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [isFiring, setIsFiring] = useState(false);
  const [hasHitCake, setHasHitCake] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(position);


  // Reset all firing state on resetCounter change
  useEffect(() => {
    // Only reset if game is not complete
    if (!gameComplete) {
      setIsFiring(false);
      setHasHitCake(false);
      setAnimationProgress(0);
      setCurrentPosition(position);
      setStartPosition(position);
      setFireVector(null);
      setWobble(floatRotationIntensity);
      if (rocketRef.current) {
        rocketRef.current.rotation.z = 0;
        rocketRef.current.rotation.y = 0;
      }
    }
  }, [resetCounter, position, floatRotationIntensity, gameComplete]);

  // Track wobble amount for firing animation
  const [wobble, setWobble] = useState(floatRotationIntensity)

  // Start firing animation when firing prop changes to true
  useEffect(() => {
    if (gameComplete) return; // Don't start firing if game is complete
    if (firing && !isFiring && !hasHitCake) {
      setStartPosition(position)
      setAnimationProgress(0)
      setIsFiring(true)
      // If firing is not an array, ensure fireVector is null (so we use the cake animation branch)
      if (!Array.isArray(firing)) {
        setFireVector(null);
      }
    }
  }, [firing, isFiring, hasHitCake, position, gameComplete])

  // Support firing as a direction vector
  const [fireVector, setFireVector] = useState(null)
  useEffect(() => {
    if (gameComplete) return;
    if (Array.isArray(firing)) {
      setFireVector(firing)
      setIsFiring(true)
      setStartPosition(position)
      setAnimationProgress(0)
      setHasHitCake(false)
    }
    // Do not handle firing === true here; let the above effect handle it and ensure fireVector is null
  }, [firing, position, gameComplete])

  useFrame((state, delta) => {
    if (paused || gameComplete) return;
    setSpin((prev) => prev + delta * 1.5);
    if (isFiring) {
      let progress = animationProgress + delta * 0.8;
      let currentPos;
      if (fail) {
        // Use shared fail endpoint and midpoint logic for both RocketFailCurve and RocketShip
        const failEnd = getFailEndPoint(startPosition, targetPosition, curveSeed);
        const bias = 0.5; // Match RocketFailCurve bias
        const mid = [
          ((1 - bias) * (startPosition[0] + failEnd[0]) / 2) + (bias * 0),
          ((1 - bias) * (startPosition[1] + failEnd[1]) / 2) + (bias * 0),
          (startPosition[2] + failEnd[2]) / 2
        ];
        const curve = new THREE.CatmullRomCurve3([
          new THREE.Vector3(...startPosition),
          new THREE.Vector3(...mid),
          new THREE.Vector3(...failEnd)
        ]);
        const curvePoints = curve.getPoints(100);
        progress = Math.min(progress, 1);
        const curveIndex = Math.floor(progress * (curvePoints.length - 1));
        const p = curvePoints[curveIndex];
        currentPos = [p.x, p.y, p.z];
        setAnimationProgress(progress);
        setCurrentPosition(currentPos);
        setWobble(floatRotationIntensity * (1 - progress));
        if (rocketRef.current) {
          rocketRef.current.rotation.z = Math.sin(progress * Math.PI * 4) * 0.1 * (1 - progress);
        }
        if (progress >= 1) {
          setIsFiring(false);
          setHasHitCake(true);
          setAnimationProgress(1);
          setCurrentPosition(failEnd);
          if (rocketRef.current) {
            rocketRef.current.rotation.z = 0;
            rocketRef.current.rotation.y = 0;
          }
          if (typeof onMiss === 'function') {
            onMiss();
          }
        }
      } else if (fireVector) {
        // Fire in arbitrary direction, continue until 40 units from start
        currentPos = [
          startPosition[0] + fireVector[0] * 40 * progress,
          startPosition[1] + fireVector[1] * 40 * progress,
          startPosition[2] + fireVector[2] * 40 * progress
        ];
        setAnimationProgress(progress);
        setCurrentPosition(currentPos);
        setWobble(floatRotationIntensity * Math.max(0, 1 - progress));
        if (rocketRef.current) {
          rocketRef.current.rotation.z = Math.sin(progress * Math.PI * 4) * 0.1 * Math.max(0, 1 - progress);
        }
        // Check distance from start
        const dx = currentPos[0] - startPosition[0];
        const dy = currentPos[1] - startPosition[1];
        const dz = currentPos[2] - startPosition[2];
        const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
        if (dist >= 40 && typeof onMiss === 'function') {
          onMiss();
        }
      } else {
        // Fire at cake: follow the same curve as RocketToCakeCurve
        progress = Math.min(progress, 1);
        // Calculate the same randomized midpoint as RocketToCakeCurve
        const combinedSeed = curveSeed + startPosition[0] * 10007 + startPosition[1] * 10009 + startPosition[2] * 10037 + targetPosition[0] * 10061 + targetPosition[1] * 10069 + targetPosition[2] * 10079;
        function seededRandom(seed) {
          let x = Math.sin(seed) * 10000;
          return x - Math.floor(x);
        }
        const mid = [
          (startPosition[0] + targetPosition[0]) / 2,
          (startPosition[1] + targetPosition[1]) / 2,
          (startPosition[2] + targetPosition[2]) / 2
        ];
        let randX = (seededRandom(combinedSeed + 1) * 6 - 3);
        let randY = (seededRandom(combinedSeed + 2) * 6 - 3);
        randX = Math.max(-3, Math.min(3, randX));
        randY = Math.max(-3, Math.min(3, randY));
        if (Math.abs(randX) < 1) randX = randX < 0 ? -1 : 1;
        if (Math.abs(randY) < 1) randY = randY < 0 ? -1 : 1;
        const randomizedMid = [mid[0] + randX, mid[1] + randY, mid[2]];
        const curve = new THREE.CatmullRomCurve3([
          new THREE.Vector3(...startPosition),
          new THREE.Vector3(...randomizedMid),
          new THREE.Vector3(...targetPosition)
        ]);
        const curvePoints = curve.getPoints(100);
        const curveIndex = Math.floor(progress * (curvePoints.length - 1));
        const p = curvePoints[curveIndex];
        currentPos = [p.x, p.y, p.z];
        setAnimationProgress(progress);
        setCurrentPosition(currentPos);
        setWobble(floatRotationIntensity * (1 - progress));
        if (rocketRef.current) {
          rocketRef.current.rotation.z = Math.sin(progress * Math.PI * 4) * 0.1 * (1 - progress);
        }
        if (progress >= 1) {
          setIsFiring(false);
          setHasHitCake(true);
          setAnimationProgress(1);
          setCurrentPosition(targetPosition);
          if (rocketRef.current) {
            rocketRef.current.rotation.z = 0;
            rocketRef.current.rotation.y = 0;
          }
          if (typeof onHitAnimationComplete === 'function') {
            onHitAnimationComplete();
          }
        }
      }
    } else if (!isFiring && !hasHitCake) {
      setWobble(floatRotationIntensity)
    }
  });


  // Dynamically find all 'Sparks' groups in the Rocket model and place emitters at their world positions


  const RocketMesh = ({ animatedPosition, showCrosshair = true, showLine = true, spinY = 0 }) => {
    // Calculate lookAtY angle to face the cake
    let lookAtY = 0;
    if (cakeRef && cakeRef.current) {
      const from = animatedPosition || position;
      const to = [cakeRef.current.position.x, cakeRef.current.position.y, cakeRef.current.position.z];
      const dx = to[0] - from[0];
      const dz = to[2] - from[2];
      lookAtY = Math.atan2(dx, dz); // Yaw angle in radians
    }
    return (
      <group
        ref={rocketRef}
        position={animatedPosition}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <group rotation={[0, lookAtY + spinY, 0]}>
          {/* Faint green line from rocket nose to crosshair, rendered behind rocket */}
          {showLine && (
            <mesh position={[0, 10, 0]} renderOrder={-1}>
              <cylinderGeometry args={[0.01, 0.01, 20, 8]} />
              <meshBasicMaterial color="#00ff00" opacity={0.2} transparent depthTest={true} />
            </mesh>
          )}

          <Miura scale={[20, 20, 20]} firing={isFiring} />

          {/* Crosshair as child of rocket, always 20 units in front of nose */}
          {showCrosshair && <Crosshair3D position={[0, 20, 0]} size={0.7} color="#00ff00" />}
        </group>
      </group>
    );
  };

  // Helper: get the front of the rocket in world space
  function getCrosshairPosition() {
    const group = rocketRef.current
    if (!group) return [0,0,0]
    // The rocket's local +Y is "forward" (nose cone at [0,1.2,0])
    // We'll project 20 units in the rocket's local +Y direction
    const localForward = new THREE.Vector3(0, 1, 0)
    localForward.applyQuaternion(group.getWorldQuaternion(new THREE.Quaternion()))
    const worldPos = new THREE.Vector3().setFromMatrixPosition(group.matrixWorld)
    return worldPos.add(localForward.multiplyScalar(20)).toArray()
  }

  // Expose getWorldQuaternion to parent via ref
  function getWorldQuaternion() {
    const group = rocketRef.current
    if (!group) return null
    return group.getWorldQuaternion(new THREE.Quaternion())
  }

  // Expose getCrosshairPosition to parent via ref
  useImperativeHandle(ref, () => ({
    getCrosshairPosition,
    getWorldQuaternion,
    rocketRef
  }))


  // Add noise-based wobble variation for idle/charging
  const [noiseTime, setNoiseTime] = useState(0);
  const [noiseOffset] = useState(() => Math.random() * 100);
  const [noisyWobble, setNoisyWobble] = useState(floatRotationIntensity);
  useFrame((_, delta) => {
    if (!isFiring && !hasHitCake) {
      const t = noiseTime + delta;
      // Use a single noise value to modulate wobble
      const n = Math.sin((t + noiseOffset) * 1.7);
      // Wobble varies between 60% and 140% of base
      setNoisyWobble(floatRotationIntensity * (1 + n * 0.9));
      setNoiseTime(t);
    }
  });
  if (!isFiring && !hasHitCake) {
    // Use Float for idle animation, but add noise-based wobble
    return (
      <Float
        speed={5}
        rotationIntensity={noisyWobble}
        floatIntensity={0}
        floatingRange={[0, 0]}
        position={position}
      >
        {/* Float animates X/Z, manual spin animates Y */}
        <RocketMesh showCrosshair={!gameComplete} showLine={!gameComplete} spinY={spin} />
      </Float>
    );
  }

  // Return rocket without Float when firing or stuck (with animated position)
  return <RocketMesh animatedPosition={currentPosition} showCrosshair={false} showLine={false} spinY={spin} />;
});
export default RocketShip;
